import React, {useEffect, useRef, useState} from 'react';
import {IconButton, InputAdornment, TextField, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import Box from '@mui/material/Box';
import {
    technicalMapApplyFiltersButtonSx,
    technicalMapFilterActionsSx,
    technicalMapFilterContentSx,
    technicalMapFilterDateRowLabelDesktopSx,
    technicalMapFilterDateRowLabelMobileSx,
    technicalMapFilterFieldSx,
    technicalMapResetFiltersButtonSx,
    technicalMapFilterRowControlSx,
    technicalMapFilterRowLabelCellSx,
    technicalMapFilterRowLabelSx,
    technicalMapFilterRowSx,
    technicalMapFilterSectionSx,
    technicalMapFilterSortLeadingSx,
    technicalMapFilterSortTrailingSx,
    technicalMapRangeFieldSx,
    technicalMapRangeRowSx,
} from './technicalMapTabletFilterStyles';
import {
    TECHNICAL_MAP_DATE_FILTER_FIELD,
    TECHNICAL_MAP_TEXT_FILTER_FIELDS,
    buildColumnFilters,
    columnFiltersToDraft,
} from './technicalMapTabletFilterUtils';
import TechnicalMapTabletFilterSortButtons from './TechnicalMapTabletFilterSortButtons';

const TechnicalMapFilterRowLabel = ({label, labelLines}) => (
    <Typography
        component="div"
        sx={technicalMapFilterRowLabelSx}
    >
        {labelLines ? labelLines.map((line) => (
            <Box key={line} component="span" sx={{display: 'block'}}>
                {line}
            </Box>
        )) : label}
    </Typography>
);

const TechnicalMapFilterDateRowLabel = ({label, labelLines}) => (
    <>
        <Typography
            component="div"
            sx={{
                ...technicalMapFilterRowLabelSx,
                ...technicalMapFilterDateRowLabelDesktopSx,
            }}
        >
            {labelLines.map((line) => (
                <Box key={line} component="span" sx={{display: 'block'}}>
                    {line}
                </Box>
            ))}
        </Typography>
        <Typography
            component="div"
            sx={{
                ...technicalMapFilterRowLabelSx,
                ...technicalMapFilterDateRowLabelMobileSx,
            }}
        >
            {label}
        </Typography>
    </>
);

const createDraftState = ({columnFilters, sorting}) => ({
    sorting: Array.isArray(sorting) ? [...sorting] : [],
    columnDraft: columnFiltersToDraft(columnFilters),
});

const TechnicalMapTabletDatePicker = ({value, onChange, onClear, placeholder}) => (
    <DatePicker
        value={value}
        onChange={onChange}
        format="DD/MM/YYYY"
        slotProps={{
            actionBar: {
                actions: ['clear'],
            },
            field: {
                placeholder,
            },
            openPickerButton: {
                sx: value ? {display: 'none'} : undefined,
            },
            textField: {
                placeholder,
                sx: {
                    ...technicalMapFilterFieldSx,
                    flex: 1,
                    minWidth: 0,
                    '& .MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
                        paddingRight: value ? '8px' : undefined,
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                        opacity: 1,
                        color: '#737478',
                    },
                },
                InputProps: value ? {
                    endAdornment: (
                        <InputAdornment
                            position="end"
                            sx={{
                                marginLeft: 0,
                                marginRight: '2px',
                                maxHeight: 'unset',
                            }}
                        >
                            <IconButton
                                size="small"
                                aria-label="Очистить дату"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={onClear}
                                edge="end"
                                sx={{
                                    p: '4px',
                                    color: '#737478',
                                }}
                            >
                                <CloseIcon sx={{fontSize: 20}}/>
                            </IconButton>
                        </InputAdornment>
                    ),
                } : undefined,
            },
        }}
        sx={{flex: 1, minWidth: 0}}
    />
);

const TechnicalMapTabletFiltersPanel = ({
    isOpen,
    columnFilters,
    sorting,
    onApply,
    onReset,
}) => {
    const wasOpenRef = useRef(false);
    const [draft, setDraft] = useState(() => createDraftState({columnFilters, sorting}));

    useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            setDraft(createDraftState({columnFilters, sorting}));
        }
        wasOpenRef.current = isOpen;
    }, [isOpen, columnFilters, sorting]);

    const handleTextFilterChange = (columnId) => (event) => {
        const value = event.target.value;
        setDraft((prev) => ({
            ...prev,
            columnDraft: {
                ...prev.columnDraft,
                [columnId]: value,
            },
        }));
    };

    const handleCompletionDateChange = (field) => (value) => {
        setDraft((prev) => ({
            ...prev,
            columnDraft: {
                ...prev.columnDraft,
                completion: {
                    ...prev.columnDraft.completion,
                    [field]: value,
                },
            },
        }));
    };

    const handleClearCompletionDate = (field) => () => {
        handleCompletionDateChange(field)(null);
    };

    const handleSortingChange = (nextSorting) => {
        setDraft((prev) => ({
            ...prev,
            sorting: nextSorting,
        }));
    };

    const handleApply = () => {
        onApply({
            columnFilters: buildColumnFilters(draft.columnDraft),
            sorting: draft.sorting,
        });
    };

    const handleReset = () => {
        setDraft(createDraftState({columnFilters: [], sorting: []}));
        onReset();
    };

    const renderSortButtons = (columnId) => (
        <TechnicalMapTabletFilterSortButtons
            columnId={columnId}
            sorting={draft.sorting}
            onSortingChange={handleSortingChange}
        />
    );

    return (
        <Box sx={technicalMapFilterSectionSx}>
            <Box sx={technicalMapFilterContentSx}>
                {TECHNICAL_MAP_TEXT_FILTER_FIELDS.map(({id, label, placeholder}) => (
                    <Box key={id} sx={technicalMapFilterRowSx}>
                        <Box sx={technicalMapFilterRowLabelCellSx}>
                            <Box sx={technicalMapFilterSortLeadingSx}>
                                {renderSortButtons(id)}
                            </Box>
                            <TechnicalMapFilterRowLabel label={label}/>
                        </Box>
                        <Box sx={technicalMapFilterRowControlSx}>
                            <TextField
                                placeholder={placeholder}
                                value={draft.columnDraft[id] ?? ''}
                                onChange={handleTextFilterChange(id)}
                                sx={{
                                    ...technicalMapFilterFieldSx,
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            />
                            <Box sx={technicalMapFilterSortTrailingSx}>
                                {renderSortButtons(id)}
                            </Box>
                        </Box>
                    </Box>
                ))}

                <Box sx={technicalMapFilterRowSx}>
                    <Box sx={technicalMapFilterRowLabelCellSx}>
                        <Box sx={technicalMapFilterSortLeadingSx}>
                            {renderSortButtons(TECHNICAL_MAP_DATE_FILTER_FIELD.id)}
                        </Box>
                        <TechnicalMapFilterDateRowLabel
                            label={TECHNICAL_MAP_DATE_FILTER_FIELD.label}
                            labelLines={TECHNICAL_MAP_DATE_FILTER_FIELD.labelLines}
                        />
                    </Box>
                    <Box sx={technicalMapFilterRowControlSx}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box sx={technicalMapRangeRowSx}>
                                <Box sx={technicalMapRangeFieldSx}>
                                    <TechnicalMapTabletDatePicker
                                        placeholder="От"
                                        value={draft.columnDraft.completion?.startDate ?? null}
                                        onChange={handleCompletionDateChange('startDate')}
                                        onClear={handleClearCompletionDate('startDate')}
                                    />
                                </Box>
                                <Box sx={technicalMapRangeFieldSx}>
                                    <TechnicalMapTabletDatePicker
                                        placeholder="До"
                                        value={draft.columnDraft.completion?.endDate ?? null}
                                        onChange={handleCompletionDateChange('endDate')}
                                        onClear={handleClearCompletionDate('endDate')}
                                    />
                                </Box>
                            </Box>
                        </LocalizationProvider>
                        <Box sx={technicalMapFilterSortTrailingSx}>
                            {renderSortButtons(TECHNICAL_MAP_DATE_FILTER_FIELD.id)}
                        </Box>
                    </Box>
                </Box>

                <Box sx={technicalMapFilterActionsSx}>
                    <Box
                        component="button"
                        type="button"
                        onClick={handleReset}
                        sx={technicalMapResetFiltersButtonSx}
                    >
                        Сброс фильтров
                    </Box>
                    <Box
                        component="button"
                        type="button"
                        onClick={handleApply}
                        sx={technicalMapApplyFiltersButtonSx}
                    >
                        Применить фильтры
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default TechnicalMapTabletFiltersPanel;
